import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

function PremiumUsersContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [memberships, setMemberships] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [memData, shopData] = await Promise.all([
        api.getAdminPremiumMemberships(statusFilter || null, shopFilter || null),
        api.getAdminShops(),
      ]);
      setMemberships(memData);
      setShops(shopData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [statusFilter, shopFilter]);

  const shopById = (id) => shops.find(s => s.id === id);

  const filtered = memberships.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = (m.user_name || m.user?.name || '').toLowerCase();
    const phone = (m.user_phone || m.user?.phone_number || '').toLowerCase();
    return name.includes(q) || phone.includes(q);
  });

  const statusBadge = (status) => {
    const base = { padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase' };
    if (status === 'active' || status === 'approved') return { ...base, background: T.green, color: T.greenText };
    if (status === 'paused' || status === 'suspended') return { ...base, background: T.red, color: T.redText };
    if (status === 'expired') return { ...base, background: T.yellow, color: T.yellowText };
    return { ...base, background: T.yellow, color: T.yellowText };
  };

  const handleSuspend = (id) => {
    showConfirm(t('admin.suspendMembershipConfirm', 'Suspend this membership?'), async () => {
      try {
        await api.adminApproveMembership(id, 'reject', 'Suspended by admin');
        fetchData();
      } catch (err) { showAlert(err.message); }
    });
  };

  const handleReactivate = (id) => {
    showConfirm(t('admin.reactivateMembershipConfirm', 'Reactivate this membership?'), async () => {
      try {
        await api.adminApproveMembership(id, 'approve');
        fetchData();
      } catch (err) { showAlert(err.message); }
    });
  };

  return (
    <AdminLayout role="admin">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={S.pageTitle}>{t('admin.premiumUsers', 'Premium Users')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange, marginBottom: '1.5rem' }} />

        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <input
            type="text"
            placeholder={t('admin.searchByNameOrPhone', 'Search by name or phone...')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...S.input, width: '250px', marginBottom: 0 }}
          />

          <select
            value={shopFilter}
            onChange={e => setShopFilter(e.target.value)}
            style={{ ...S.select, width: '200px', fontSize: '0.82rem', marginBottom: 0 }}
          >
            <option value="">{t('admin.allSalons', 'All Salons')}</option>
            {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ ...S.select, width: '200px', fontSize: '0.82rem', marginBottom: 0 }}
          >
            <option value="active">{t('admin.active', 'Active')}</option>
            <option value="paused">{t('admin.paused', 'Paused')}</option>
            <option value="">{t('admin.allStatuses', 'All Statuses')}</option>
          </select>

          {(search || shopFilter || statusFilter !== 'active') && (
            <button
              onClick={() => { setSearch(''); setShopFilter(''); setStatusFilter('active'); }}
              style={{ ...S.btnOutline, padding: '0.4rem 0.8rem', fontSize: '0.75rem', height: '36px' }}
            >
              {t('admin.clearFilters', 'Clear Filters')}
            </button>
          )}
        </div>
      </div>

      {loading ? <p style={{ color: T.muted }}>{t('common.loading', 'Loading...')}</p> : filtered.length === 0 ? (
        <p style={{ color: T.muted, fontStyle: 'italic' }}>{t('admin.noPremiumUsers', 'No premium users found.')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {[
                  t('admin.user', 'User'),
                  t('admin.salon', 'Salon'),
                  t('admin.plan', 'Plan'),
                  t('admin.since', 'Since'),
                  t('admin.nextPayment', 'Next Payment'),
                  t('admin.status', 'Status'),
                  t('admin.actions', 'Actions'),
                ].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => {
                const shop = shopById(m.shop_id);
                const isActive = m.status === 'active' || m.status === 'approved';
                return (
                  <tr key={m.id} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(14,165,233,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '50%',
                          background: T.orange, color: '#fff', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 700,
                        }}>
                          {(m.user_name || m.user?.name || '?')[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: T.white }}>{m.user_name || m.user?.name || `User #${m.user_id}`}</div>
                          <div style={{ fontSize: '0.78rem', color: T.muted }}>{m.user_phone || m.user?.phone_number || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...S.td, color: T.orange, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', letterSpacing: '0.05em' }}>
                      {shop?.name || `Shop #${m.shop_id}`}
                    </td>
                    <td style={S.td}>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.82rem' }}>
                        {m.plan_name || 'VIP Monthly'}
                      </span>
                      {m.monthly_price && <div style={{ fontSize: '0.75rem', color: T.muted }}>{m.monthly_price} EGP/mo</div>}
                    </td>
                    <td style={S.td}>
                      {m.start_date ? new Date(m.start_date).toLocaleDateString() : m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={S.td}>
                      {m.next_payment_date ? new Date(m.next_payment_date).toLocaleDateString() : '—'}
                    </td>
                    <td style={S.td}>
                      <span style={statusBadge(m.status)}>{m.status}</span>
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {isActive && (
                          <button
                            onClick={() => handleSuspend(m.id)}
                            style={S.btnDanger}
                          >
                            {t('admin.suspend', 'Suspend')}
                          </button>
                        )}
                        {!isActive && (
                          <button
                            onClick={() => handleReactivate(m.id)}
                            style={{
                              background: T.greenText, color: '#fff', border: 'none',
                              borderRadius: '4px', padding: '0.35rem 0.85rem',
                              fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem',
                              fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
                            }}
                          >
                            {t('admin.reactivate', 'Reactivate')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function PremiumUsers() {
  return <ProtectedRoute requireSuperAdmin><PremiumUsersContent /></ProtectedRoute>;
}
